/* eslint-disable no-dupe-else-if */
import {
  KICKOFF_INPUTS,
  KICKOFF_YARDLINE,
  PAT_INPUTS,
  PRIMARY_FLOW_INPUTS,
  REF_EVENT_INPUTS,
  TEventTypes,
  TPlayDesigns,
  TPlayResultTypes,
  TQuarters,
  TWO_PT_ATTEMPT_INP,
  TWO_PT_RET_ONE_PT_SAFTEY_INP,
  XP_ATTEMPT_INP,
} from '@/constants';
import {
  IConfirmationInput,
  IMachineContext,
  ITwoRowGenericSelectorInp,
  TColors,
  TMachineEvents,
  TMetaFuncInput,
  TOffDef,
  TPatEvents,
  TPlayResults,
  TPlays,
  TRefEventsActions,
  TWorkflowType,
} from '@/types';
import { TPATPlayEvents } from '@/types/workflow/patMachine';
import { refEventValue } from '@/utilities';
import {
  handleTwoPtConfirmation,
  handleXPConfirmation,
  handleXPReturnConfirmation,
  sendPenaltyEvent,
  startPatWFAlternate,
} from '@/workflow/utils';
import { cloneDeep, last } from 'lodash';

export const onPatDesignSelected = ({
  send,
  payload,
  generalState,
  playFlowState,
  context,
}: TMetaFuncInput) => {
  if (payload.value === TPatEvents.TWO_PT_ATTEMPT) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.PLAY_DESIGN,
        value: TPlayDesigns.TWO_PT_CONVERSION,
      },
      generalState.currentGame.gameId
    );

    send(TPATPlayEvents.TO_TWO_PT_ATTEMPT, {
      nextStateInput: {
        topJustify: 'start',
        topOptions: [
          {
            text: '2pt SNAP',
            value: '',
            col: 12,
          },
        ],
        bottomJustify: 'end',
        bottomOptions: [
          {
            text: TPlays.REF,
            value: TPlays.REF,
            color: TColors.YELLOW,
            col: 4,
          },
        ],
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.TWO_PT_CONVERSION,
        },
      },
    });
  } else if (payload.value === TPatEvents.XP_ATTEMPT) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.PLAY_DESIGN,
        value: TPlayDesigns.EXTRA_PT,
      },
      generalState.currentGame.gameId
    );

    send(TPATPlayEvents.TO_XP_ATTEMPT, {
      nextStateInput: {
        topJustify: 'start',
        topOptions: [
          {
            text: 'XP SNAP',
            value: '',
            col: 12,
          },
        ],
        bottomJustify: 'end',
        bottomOptions: [
          {
            text: TPlays.REF,
            value: TPlays.REF,
            color: TColors.YELLOW,
            col: 4,
          },
        ],
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.EXTRA_PT,
        },
      },
    });
  } else if (payload.value === TPlays.REF) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TPATPlayEvents.REF_EVENT, {
      nextStateInput: {
        ...REF_EVENT_INPUTS,
        backTo: TPATPlayEvents.BACK_TO_PAT_PLAY_DESIGN,
      } as ITwoRowGenericSelectorInp,
      ctxUpdate: {
        currentPlay: {
          ...context.currentPlay,
          playDesignId: TPlayDesigns.NO_PLAY,
        },
      },
    });
  }
};

export const onPatSnapped = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.value === TPlays.REF) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TPATPlayEvents.REF_EVENT, {
      nextStateInput: {
        ...REF_EVENT_INPUTS,
        backTo: TRefEventsActions.BACK_TO_XP_SNAP,
      } as ITwoRowGenericSelectorInp,
    });
  }

  playFlowState.pushEvent(
    {
      eventTypeId: TEventTypes.BALL_SNAP,
      value: true,
    },
    generalState.currentGame.gameId
  );

  send(TPATPlayEvents.TO_XP_ATTEMPT, {
    ctxUpdate: {
      ...context,
      currentPlay: {
        ...context.currentPlay,
        options: {
          ...context.currentPlay.options,
          isKickoffSnapped: true,
        },
      },
    },
    nextStateInput: XP_ATTEMPT_INP,
  });
};

export const onXpAttempt = ({ send, payload, playFlowState, generalState, context }: TMetaFuncInput) => {
  const nextStateInput: IConfirmationInput = {};
  if (payload.toggles[TPlays.PENALTY_ALERT]) {
    let result = null;
    if (payload.value === TPatEvents.XP_MADE) {
      result = TPlayResultTypes.EXTRA_PT;
    } else if (payload.value === TPatEvents.XP_MADE) {
      result = TPlayResultTypes.PAT_MISS;
    }

    sendPenaltyEvent(
      playFlowState,
      generalState,
      send,
      { playDesignId: TPlayDesigns.EXTRA_PT, resultType: [result] },
      TPATPlayEvents.PAT_PENALTY,
      TRefEventsActions.BACK_TO_XP
    );
  } else if ([TPatEvents.XP_MADE, TPatEvents.XP_MISS].includes(payload.value)) {
    handleXPConfirmation(send, nextStateInput, payload, context, playFlowState);
  } else if (payload.value === TPatEvents.XP_RETURN) {
    handleXPReturnConfirmation(send, nextStateInput, context, playFlowState);
  }
};

export const on2ptAttemptSnap = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  if (payload.value === TPlays.REF) {
    playFlowState.pushEvent(
      {
        eventTypeId: TEventTypes.REF_EVENT,
        value: refEventValue(playFlowState.driveNumber, playFlowState.drivePlayNumber),
      },
      generalState.currentGame.gameId
    );

    send(TPATPlayEvents.REF_EVENT, {
      nextStateInput: {
        ...REF_EVENT_INPUTS,
        backTo: TRefEventsActions.BACK_TO_TWO_PT_SNAP,
      } as ITwoRowGenericSelectorInp,
    });
  }

  playFlowState.pushEvent(
    {
      eventTypeId: TEventTypes.BALL_SNAP,
      value: true,
    },
    generalState.currentGame.gameId
  );

  send(TPATPlayEvents.TO_TWO_PT_ATTEMPT, {
    ctxUpdate: {
      ...context,
      currentPlay: {
        ...context.currentPlay,
        options: {
          ...context.currentPlay.options,
          isKickoffSnapped: true,
        },
      },
    },
    nextStateInput: TWO_PT_ATTEMPT_INP,
  });
};

export const on2ptAttempt = ({ send, payload, playFlowState, generalState, context }: TMetaFuncInput) => {
  if (payload.toggles[TPlays.PENALTY_ALERT]) {
    let result = null;
    if (payload.value === TPlayResults.TWO_PT_MADE) {
      result = TPlayResultTypes.CONVERSION_2PT;
    } else if (payload.value === TPlayResults.TWO_PT_MISS) {
      result = TPlayResultTypes.PAT_MISS;
    }

    sendPenaltyEvent(
      playFlowState,
      generalState,
      send,
      { playDesignId: TPlayDesigns.TWO_PT_CONVERSION, resultType: [result] },
      TPATPlayEvents.PAT_PENALTY,
      TRefEventsActions.BACK_TO_2PT
    );
  } else if ([TPlayResults.TWO_PT_MADE, TPlayResults.TWO_PT_MISS].includes(payload.value)) {
    handleTwoPtConfirmation(send, payload, context, playFlowState);
  } else if (payload.value === TPatEvents.TWO_PT_RET_ONE_PT_SAFTEY) {
    send(TPATPlayEvents.ON_TWO_PT_RETURN_ONE_PT_SAFTEY, {
      nextStateInput: TWO_PT_RET_ONE_PT_SAFTEY_INP,
    });
  }
};

export const onPatConfirmed = ({ send, payload, generalState, playFlowState, context }: TMetaFuncInput) => {
  const ctxUpdate: Partial<IMachineContext> = {
    currentWorkflow: null,
    currentPlay: {
      ...context.currentPlay,
      resultType: cloneDeep(context.currentPlay?.resultType || []),
    },
  };

  // redo pat if penalty pre snap
  if (context.currentPlay.options?.isPreSnapPATPenalty || context.currentPlay.penalty === TOffDef.DEFENSE) {
    send(TMachineEvents.TO_PAT, {
      nextStateInput: PAT_INPUTS,
      ctxUpdate: {
        currentWorkflow: TWorkflowType.PAT,
      },
    });
  }

  if (payload.toggles[TPlayResults.EXTRA_POINT]) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.EXTRA_PT];
  } else if (payload.toggles[TPlayResults.TWO_PT_CONVERSION]) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.CONVERSION_2PT];
  } else if (payload.toggles[TPlayResults.TWO_PT_RETURN]) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.TWO_PT_RETURN];
    ctxUpdate.currentPlay.postPlayYardLine = KICKOFF_YARDLINE;
    ctxUpdate.currentPlay.options = {
      ...ctxUpdate.currentPlay?.options,
      patReturned: true,
    };
  } else if (payload.toggles[TPlayResults.ONE_PT_SAFTEY]) {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.SAFTEY_1PT];
  } else {
    ctxUpdate.currentPlay.resultType = [TPlayResultTypes.PAT_MISS];
  }

  ctxUpdate.currentPlay.options = {
    ...ctxUpdate.currentPlay.options,
    isPatOrFgMake: true,
  };

  if (playFlowState.currentQuarter > TQuarters.OT2) {
    startPatWFAlternate({ send, payload, generalState, playFlowState, context });
  } else if (playFlowState.currentQuarter > TQuarters.Q4) {
    send(TPATPlayEvents.OVERTIME_PAT_COMPLETE, {
      nextStateInput: PRIMARY_FLOW_INPUTS,
      ctxUpdate: {
        ...ctxUpdate,
        currentWorkflow: null,
      },
    });
  } else {
    send(TPATPlayEvents.PAT_COMPLETED, {
      nextStateInput: KICKOFF_INPUTS(),
      ctxUpdate: {
        ...ctxUpdate,
        currentWorkflow: TWorkflowType.KICKOFF,
      },
    });
  }
};
